/**
 * useCampaign Hook
 * 
 * React hook for managing a single campaign with real-time updates.
 * Wraps CampaignRepository for data access and delegates complex operations to Cloud Functions.
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { CampaignRepository } from '../models';

/**
 * Hook for managing a campaign
 * @param {string} campaignId - Campaign ID to manage
 * @returns {Object} - Campaign data and operations
 */
export const useCampaign = (campaignId) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const repo = new CampaignRepository(db, functions);

  // Real-time campaign listener
  useEffect(() => {
    if (!campaignId) {
      setLoading(false);
      return;
    }


    const unsubscribe = onSnapshot(
      doc(db, 'discovery_campaigns', campaignId),
      (docSnap) => {
        if (docSnap.exists()) {
          const campaignData = {
            id: docSnap.id,
            ...docSnap.data(),
          };
          setCampaign(campaignData);
          setError(null);
        } else {
          setCampaign(null);
          setError(new Error('Campaign not found'));
        }
        setLoading(false);
      },
      (err) => {
        console.error('🔧 [NEW ARCH] useCampaign: Error in listener', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [campaignId]);

  /**
   * Delete campaign (cascade to mailshots)
   * Delegates to Cloud Function for atomic operation
   */
  const deleteCampaign = useCallback(async () => {
    try {
      await repo.delete(campaignId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error deleting campaign:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Update campaign fields
   */
  const update = useCallback(async (updates) => {
    try {
      await repo.update(campaignId, updates);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error updating campaign:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Add mailshot to campaign
   */
  const addMailshot = useCallback(async (mailshotId) => {
    try {
      await repo.addMailshot(campaignId, mailshotId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error adding mailshot:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Remove mailshot from campaign
   */
  const removeMailshot = useCallback(async (mailshotId) => {
    try {
      await repo.removeMailshot(campaignId, mailshotId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error removing mailshot:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Complete campaign
   */
  const complete = useCallback(async () => {
    try {
      await repo.complete(campaignId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error completing campaign:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Archive campaign
   */
  const archive = useCallback(async () => {
    try {
      await repo.archive(campaignId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error archiving campaign:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Reactivate campaign
   */
  const reactivate = useCallback(async () => {
    try {
      await repo.reactivate(campaignId);
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error reactivating campaign:', error);
      throw error;
    }
  }, [campaignId]);

  /**
   * Refresh campaign data manually
   */
  const refresh = useCallback(async () => {
    try {
      const updatedCampaign = await repo.findById(campaignId);
      if (updatedCampaign) {
        setCampaign(updatedCampaign.toJSON());
      }
    } catch (error) {
      console.error('🔧 [NEW ARCH] useCampaign: Error refreshing campaign:', error);
      setError(error);
    }
  }, [campaignId]);

  return {
    campaign,
    loading,
    error,
    deleteCampaign,
    update,
    addMailshot,
    removeMailshot,
    complete,
    archive,
    reactivate,
    refresh,
  };
};
